/**
 * Which `role="dialog"` elements are actually *open*.
 *
 * `document.querySelector('[role="dialog"]')` is not the same question. The app
 * shell keeps its mobile sidebar mounted at all times so it can animate, with
 * `role="dialog"` on it permanently; when closed it is marked `inert` and
 * `aria-hidden`, and `lg:hidden` also gives it `display: none` on desktop.
 *
 * A plain query therefore matches on *every page, always*. That silently
 * disabled the service-worker auto-update, whose safety check treats any open
 * dialog as "user is mid-task" — the condition was never false, so an idle tab
 * never took a pending update and users sat on a stale build behind an
 * "A new version is available" banner until they clicked it by hand.
 *
 * The signals below are the ones the shell itself sets to mean "parked", and
 * they are the same three MessagingPanel uses to decide whether a dialog is
 * stacked above it. Geometry is deliberately not consulted: jsdom performs no
 * layout, so anything measured that way cannot be tested. A closed `Modal` is
 * unmounted rather than hidden, so there is no third state to worry about.
 */
function isParked(el: Element): boolean {
  if (el.closest('[inert]')) return true;
  if (el.getAttribute('aria-hidden') === 'true') return true;
  if (typeof getComputedStyle === 'function' && getComputedStyle(el).display === 'none') return true;
  return false;
}

/** Every dialog that is genuinely open, in DOM order. */
export function getOpenDialogs(): HTMLElement[] {
  if (typeof document === 'undefined') return [];
  return Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"]')).filter(
    (el) => !isParked(el)
  );
}

/**
 * Is any dialog open, ignoring `except`?
 *
 * Pass the caller's own dialog as `except` to ask "is something stacked on top
 * of me" — the shared Modal portals to `document.body`, so a stacked modal is a
 * sibling rather than a descendant and containment checks cannot see it.
 */
export function hasOpenDialog(except?: Element | null): boolean {
  return getOpenDialogs().some((el) => el !== except);
}
